import { userPreferencesFavoriteSearchApi } from "@/main/api";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { DELAYED_CARD_STATUS_KEY } from "@/main/services/constants/constantsCardStatus";
import { ProcurementStatusEnum } from "@/main/services/enum/StatusProcurement";
import { isEmpty } from "@/main/services/utils";
import { z } from "zod";

export const KeyValueSchema = z.object({
	key: z.union([z.string(), z.number()]),
	value: z.string(),
});

export const formSchema = z.object({
	quoteFilterVehicle: z.string().optional(),
	idQuoteStatus: z.array(KeyValueSchema).optional(),
	states: z.array(KeyValueSchema).optional(),
	reason: KeyValueSchema.nullable().optional(),
	insurer: KeyValueSchema.nullable().optional(),
	supplier: KeyValueSchema.nullable().optional(),
	bodyshopBranches: z.array(KeyValueSchema).optional(),
	subModel: z.string().optional(),
	vehicleManufacturer: z.string().optional(),
	vehicleModel: z.string().optional(),
	vehicle: z.string().optional(),
	idPreQuoteType: KeyValueSchema.nullable().optional(),
	havingItemCanceledBySupplier: z.boolean().optional(),
	havingDelayedItem: z.boolean().optional(),
	orderByDelay: z.boolean().optional(),
	sortByOldest: KeyValueSchema.nullable().optional(),
	backOrder: z.boolean().optional(),
	selfSupplied: z.boolean().optional(),
});

export type TypeFavoriteSearchOrder = z.infer<typeof formSchema>;

export function applyDelayedCardFilters<T extends { havingDelayedItem?: boolean; orderByDelay?: boolean }>(
	request: T,
): T {
	request.havingDelayedItem = true;
	request.orderByDelay = true;
	return request;
}

export function splitDelayedStatusSelection(statuses?: { key: string | number }[] | null): {
	statusIds: number[];
	delayedSelected: boolean;
} {
	const selected = statuses ?? [];
	const isDelayed = (key: string | number) => String(key) === DELAYED_CARD_STATUS_KEY;

	return {
		statusIds: selected.filter((status) => !isDelayed(status.key)).map((status) => Number(status.key)),
		delayedSelected: selected.some((status) => isDelayed(status.key)),
	};
}

export function getDropdownStatusOptions<T extends { key: string | number }>(
	options: T[],
	showDelayedCard: boolean,
	delayedOption: T,
): T[] {
	return showDelayedCard ? positionDelayedCard([...options, delayedOption]) : options;
}

export function getSupplierDropdownStatusOptions<T extends { key: string | number }>(
	options: T[],
	showDelayedCard: boolean,
	delayedOption: T,
): T[] {
	const filtered = options.filter(
		(option) =>
			Number(option.key) !== ProcurementStatusEnum.Open && Number(option.key) !== ProcurementStatusEnum.Manual,
	);

	return getDropdownStatusOptions(filtered, showDelayedCard, delayedOption);
}

export function positionDelayedCard<T extends { key: string | number }>(cards: T[]): T[] {
	const delayedIndex = cards.findIndex((card) => String(card.key) === DELAYED_CARD_STATUS_KEY);
	if (delayedIndex === -1) return cards;

	const delayedCard = cards[delayedIndex];
	const remaining = [...cards.slice(0, delayedIndex), ...cards.slice(delayedIndex + 1)];

	const deliveredIndex = remaining.findIndex((card) => Number(card.key) === ProcurementStatusEnum.Delivered);
	if (deliveredIndex !== -1) {
		return [...remaining.slice(0, deliveredIndex), delayedCard, ...remaining.slice(deliveredIndex)];
	}

	const transitIndex = remaining.findIndex((card) => Number(card.key) === ProcurementStatusEnum.Transit);
	if (transitIndex !== -1) {
		return [...remaining.slice(0, transitIndex + 1), delayedCard, ...remaining.slice(transitIndex + 1)];
	}

	return [...remaining, delayedCard];
}

export function mapFavoriteToForm(data: any): TypeFavoriteSearchOrder {
	return {
		quoteFilterVehicle: data.quoteFilterVehicle ?? "",
		idQuoteStatus: data.idQuoteStatus ?? [],
		states: data.states ?? [],
		insurer: data.insurer ?? null,
		supplier: data.supplier ?? null,
		bodyshopBranches: data.bodyshopBranches ?? [],
		subModel: data.subModel ?? "",
		vehicleManufacturer: data.vehicleManufacturer ?? "",
		vehicleModel: data.vehicleModel ?? "",
		vehicle: data.vehicle ?? "",
		idPreQuoteType: data.idPreQuoteType ?? null,
		havingItemCanceledBySupplier: !!data.havingItemCanceledBySupplier,
		havingDelayedItem: !!data.havingDelayedItem,
		orderByDelay: !!data.orderByDelay,
		sortByOldest: data.sortByOldest ?? null,
		backOrder: !!data.backOrder,
		selfSupplied: !!data.selfSupplied,
	};
}

export async function saveFavoriteSearch(data: TypeFavoriteSearchOrder | null): Promise<boolean> {
	try {
		await userPreferencesFavoriteSearchApi.saveUserPreferencesFavoriteSearch({
			routeUrl: RoutesConstants.orderSearchOrder,
			jsonData: data ? JSON.stringify(data) : "",
		});

		return true;
	} catch {
		return false;
	}
}

export async function getFavoriteSearch(): Promise<TypeFavoriteSearchOrder | null> {
	try {
		const response = await userPreferencesFavoriteSearchApi.getUserPreferencesFavoriteSearch<any>(
			RoutesConstants.orderSearchOrder,
		);

		if (isEmpty(response?.data?.result)) return null;

		return mapFavoriteToForm(response.data.result);
	} catch {
		return null;
	}
}

export type TypePredefinedSearch = z.infer<typeof formSchema>;

export function mapPredefinedSearchToForm(data: any): TypePredefinedSearch {
	return {
		quoteFilterVehicle: data.quoteFilterVehicle ?? "",
		idQuoteStatus: data.idQuoteStatus ?? [],
		states: data.states ?? [],
		insurer: data.insurer ?? null,
		supplier: data.supplier ?? null,
		bodyshopBranches: data.bodyshopBranches ?? [],
		subModel: data.subModel ?? "",
		vehicleManufacturer: data.vehicleManufacturer ?? "",
		vehicleModel: data.vehicleModel ?? "",
		vehicle: data.vehicle ?? "",
		idPreQuoteType: data.idPreQuoteType ?? null,
		havingItemCanceledBySupplier: !!data.havingItemCanceledBySupplier,
		havingDelayedItem: !!data.havingDelayedItem,
		orderByDelay: !!data.orderByDelay,
		sortByOldest: data.sortByOldest ?? null,
		backOrder: !!data.backOrder,
		selfSupplied: !!data.selfSupplied,
	};
}

export async function saveUserPredefinedSearch(
	data: TypePredefinedSearch | null,
	description: string,
): Promise<boolean> {
	try {
		await userPreferencesFavoriteSearchApi.saveUserPredefinedSearch({
			routeUrl: RoutesConstants.orderSearchOrder,
			jsonData: data ? JSON.stringify(data) : "",
			description,
		});

		return true;
	} catch {
		return false;
	}
}

export async function getDetailsUserPredefinedSearch(
	guidUserPredefinedSearch: string,
): Promise<TypePredefinedSearch | null> {
	try {
		const response =
			await userPreferencesFavoriteSearchApi.getDetailsUserPredefinedSearch<any>(guidUserPredefinedSearch);

		if (response?.data?.result) {
			return mapPredefinedSearchToForm(response.data.result);
		}

		return null;
	} catch {
		return null;
	}
}

export async function deleteUserPredefinedSearch(guidUserPredefinedSearch: string): Promise<boolean> {
	try {
		await userPreferencesFavoriteSearchApi.deleteUserPredefinedSearch(guidUserPredefinedSearch);
		return true;
	} catch {
		return false;
	}
}

export function groupInPairs<T>(items: T[]): T[][] {
	const pairs: T[][] = [];
	for (let index = 0; index < items.length; index += 2) {
		pairs.push(items.slice(index, index + 2));
	}
	return pairs;
}
